import { useEffect, useMemo, useRef, useState } from "react";
import { getBytes } from "../../../utils/idb";

export const useTexturePreviewUrlByKey = ({ textures, assetEntries }) => {
  const idbTextureObjectUrlCacheRef = useRef(new Map());
  const [idbTexturePreviewUrls, setIdbTexturePreviewUrls] = useState({});

  const idbTexturePreviewSourceKey = useMemo(() => {
    return Object.entries(textures || {})
      .map(([textureUuid, texture]) => {
        if (!texture) return "";
        const fingerprint = texture?.fingerprint;
        const assetEntry = fingerprint ? assetEntries?.[fingerprint] : undefined;
        const idbKey = assetEntry?.sources?.idbKey;
        if (!idbKey) return "";
        return `${textureUuid}\u0000${texture?.name || ""}\u0000${idbKey}`;
      })
      .filter(Boolean)
      .sort()
      .join("\u0001");
  }, [textures, assetEntries]);

  useEffect(() => {
    let disposed = false;
    const createdThisRun = [];

    const loadIdbTexturePreviewUrls = async () => {
      const candidates = Object.entries(textures || {})
        .map(([textureUuid, texture]) => {
          if (!texture) return null;
          const fingerprint = texture?.fingerprint;
          const assetEntry = fingerprint ? assetEntries?.[fingerprint] : undefined;
          const idbKey = assetEntry?.sources?.idbKey;
          return idbKey ? { textureUuid, textureName: texture?.name || "", idbKey } : null;
        })
        .filter(Boolean);

      const cache = idbTextureObjectUrlCacheRef.current;
      const activeIdbKeys = new Set(candidates.map((candidate) => candidate.idbKey));

      if (candidates.length === 0) {
        if (!disposed) setIdbTexturePreviewUrls({});
        for (const [idbKey, blobUrl] of cache.entries()) {
          URL.revokeObjectURL(blobUrl);
          cache.delete(idbKey);
        }
        return;
      }

      try {
        const next = {};
        for (const { textureUuid, textureName, idbKey } of candidates) {
          let blobUrl = cache.get(idbKey);
          if (!blobUrl) {
            try {
              const bytes = await getBytes(idbKey);
              if (!bytes) continue;
              const blob = bytes instanceof Blob ? bytes : new Blob([bytes]);
              blobUrl = URL.createObjectURL(blob);
              createdThisRun.push(blobUrl);
              cache.set(idbKey, blobUrl);
            } catch {
              // Ignore individual texture preview failures.
              continue;
            }
          }

          next[textureUuid] = blobUrl;
          if (textureName && !next[textureName]) {
            next[textureName] = blobUrl;
          }
        }

        if (!disposed) {
          setIdbTexturePreviewUrls(next);
          for (const [idbKey, blobUrl] of cache.entries()) {
            if (!activeIdbKeys.has(idbKey)) {
              URL.revokeObjectURL(blobUrl);
              cache.delete(idbKey);
            }
          }
        } else {
          createdThisRun.forEach((url) => {
            URL.revokeObjectURL(url);
            for (const [idbKey, cachedUrl] of cache.entries()) {
              if (cachedUrl === url) cache.delete(idbKey);
            }
          });
        }
      } catch {
        if (!disposed) setIdbTexturePreviewUrls({});
      }
    };

    loadIdbTexturePreviewUrls();

    return () => {
      disposed = true;
    };
  }, [idbTexturePreviewSourceKey, textures, assetEntries]);

  useEffect(() => {
    const cache = idbTextureObjectUrlCacheRef.current;
    return () => {
      for (const blobUrl of cache.values()) {
        URL.revokeObjectURL(blobUrl);
      }
      cache.clear();
    };
  }, []);

  return useMemo(() => {
    const isDirectPreviewUrl = (url) => {
      if (!url || typeof url !== "string") return false;
      const value = url.trim();
      return (
        value.startsWith("blob:") ||
        value.startsWith("data:") ||
        value.startsWith("/") ||
        value.startsWith("http://") ||
        value.startsWith("https://")
      );
    };
    const pickAssetUrl = (assetEntry) =>
      assetEntry?.sources?.url || assetEntry?.sources?.blobUrl || "";

    const byKey = {};
    Object.entries(textures || {}).forEach(([textureUuid, texture]) => {
      if (!texture) return;
      const fingerprint = texture?.fingerprint;
      const assetEntry = fingerprint ? assetEntries?.[fingerprint] : undefined;
      const directTextureUrl = isDirectPreviewUrl(texture?.url) ? texture.url : "";
      const hasDurableIdbPreview = !!assetEntry?.sources?.idbKey;
      const previewUrl = hasDurableIdbPreview
        ? idbTexturePreviewUrls[textureUuid] || ""
        : pickAssetUrl(assetEntry) || directTextureUrl || "";
      if (!previewUrl) return;
      byKey[textureUuid] = previewUrl;
      if (texture?.name && !byKey[texture.name]) {
        byKey[texture.name] = previewUrl;
      }
    });
    return byKey;
  }, [textures, assetEntries, idbTexturePreviewUrls]);
};
